import { Link } from "react-router-dom";
import { Seo } from "@/components/Seo";
import { Reveal, ArchReveal, MaskedLines, Marquee } from "@/components/motion";
import { IMG, FALLBACK_IMG } from "@/lib/images";
import { ArrowRight } from "lucide-react";

const CHAPTERS = [
  {
    no: "01", title: "Sıcaklık", img: IMG.hero_secondary,
    text: "Shule bir tezgâhta değil, bir sofrada doğdu: sevdiklerimizle paylaştığımız anların, takıldığımız küpelerle hatırlandığını fark ettiğimiz bir akşamda. O günden beri her parça, bir ana eşlik etmek için tasarlanıyor.",
  },
  {
    no: "02", title: "Zanaat", img: IMG.ring_hand,
    text: "Her tasarım, İstanbul'daki atölyemizde elden geçer. Kaplama kalınlığından taşın oturuşuna kadar her detay, günlük hayatın ritmine dayanacak şekilde düşünülür. Premium his, ulaşılabilir fiyat: bu denge bizim imzamız.",
  },
  {
    no: "03", title: "Anlar", img: IMG.model_shirt,
    text: "Bir iş görüşmesi, bir ilk buluşma, bir 'sadece öyle' günü. Doğru aksesuarın en büyük hikâyelere değil, en gerçek anlara eşlik ettiğine inanıyoruz. Bu yüzden sloganımız bir vaat değil, bir davet: Her Anında Sen.",
  },
];

export default function About() {
  return (
    <>
      <Seo title="Hakkımızda" description="Shule'nin hikâyesi: sıcaklık, zanaat ve anlar." path="/hakkimizda" />
      <section className="container-shule py-16 lg:py-24 text-center max-w-3xl mx-auto">
        <p className="eyebrow">Manifesto</p>
        <h1 className="mt-4 font-serif text-5xl lg:text-7xl font-light leading-[1.1] text-shule-coffee text-balance">
          <MaskedLines lines={["Aksesuar değil,", "anların"]} />
          <span className="italic text-shule-goldhover">sessiz tanığı.</span>
        </h1>
      </section>

      <div className="border-y border-shule-line bg-shule-cream/70 py-4 select-none">
        <Marquee>
          {["Sıcaklık", "Zanaat", "Anlar", "Her Anında Sen"].map((w) => (
            <span key={w} className="inline-flex items-center">
              <span className="font-serif italic text-2xl text-shule-coffee/70 px-6">{w}</span>
              <span className="w-1.5 h-1.5 rounded-full bg-shule-gold/70" />
            </span>
          ))}
        </Marquee>
      </div>

      <div className="container-shule py-16 lg:py-24 space-y-20 lg:space-y-28">
        {CHAPTERS.map((c, i) => (
          <div key={c.no} className={`grid lg:grid-cols-2 gap-10 lg:gap-16 items-center ${i % 2 === 1 ? "lg:[&>*:first-child]:order-2" : ""}`}>
            <Reveal>
              <p className="font-serif italic text-6xl lg:text-7xl text-shule-goldlight">{c.no}</p>
              <h2 className="mt-2 font-serif text-4xl text-shule-coffee">{c.title}</h2>
              <p className="mt-5 text-shule-mocha leading-relaxed max-w-md">{c.text}</p>
            </Reveal>
            <ArchReveal delay={0.15}>
              <div className="arch-card max-w-md w-full">
                <img src={c.img} alt={c.title} loading="lazy" onError={(e) => { e.currentTarget.src = FALLBACK_IMG; }}
                  className="w-full aspect-[4/5] object-cover" />
              </div>
            </ArchReveal>
          </div>
        ))}
      </div>

      <section className="bg-shule-pudra/40 border-t border-shule-line">
        <div className="container-shule py-16 lg:py-24">
          <Reveal className="text-center mb-10">
            <p className="eyebrow">Mağaza Dünyamız</p>
            <h2 className="mt-2 font-serif text-3xl sm:text-4xl text-shule-coffee">Daima seninle — her formatta</h2>
            <p className="mt-3 text-sm text-shule-soft max-w-lg mx-auto">Açık meşe, kırık beyaz, pudra pembe ve rose gold detaylarla tasarlanan mağaza ve kiosk konseptlerimiz.</p>
          </Reveal>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { src: "/assets/img/store-90.jpeg", label: "90 m² Mağaza" },
              { src: "/assets/img/store-150.jpeg", label: "150 m² Mağaza" },
              { src: "/assets/img/store-200.jpeg", label: "200 m² Mağaza" },
              { src: "/assets/img/kiosk-concept.jpeg", label: "3 × 3 m Kiosk" },
            ].map((f, i) => (
              <ArchReveal key={f.label} delay={i * 0.07}>
                <figure data-testid={`brand-format-${i}`} className="arch-card">
                  <img src={f.src} alt={`Shule ${f.label} konsepti`} loading="lazy"
                    className="w-full aspect-[3/4] object-cover object-top hover:scale-[1.04] transition-transform duration-700" />
                  <figcaption className="sr-only">{f.label}</figcaption>
                </figure>
              </ArchReveal>
            ))}
          </div>
        </div>
      </section>

      <section className="bg-shule-pudra/40 border-t border-shule-line">
        <div className="container-shule py-16 text-center bg-shule-base">
          <Reveal>
            <h2 className="font-serif text-3xl sm:text-4xl text-shule-coffee">Hikâyenin bir parçası ol</h2>
            <div className="mt-6 flex flex-wrap justify-center gap-3">
              <Link to="/kategori/kolyeler" data-testid="about-explore-cta" className="btn-primary-shule">Koleksiyonu Keşfet <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/bayilik" data-testid="about-franchise-cta" className="btn-outline-shule">Bayilik Başvurusu</Link>
            </div>
          </Reveal>
        </div>
      </section>
    </>
  );
}
